<template>
    <div v-if="bulk_message">
        <v-text-field
            autocomplete="off"
            label="Search"
            prepend-icon="mdi-magnify"
            :value="full_text"
            clearable
            @input="onSearchInputDebounced"
        >
            <template #append-outer>
                <div class="d-flex gap-3 align-center">
                    <v-tooltip bottom>
                        <template #activator="{ on, attrs }">
                            <v-icon
                                v-bind="attrs"
                                v-on="on"
                                class="mr-0"
                                @click="$emit('update')"
                            >
                                mdi-refresh
                            </v-icon>
                        </template>
                        <span>Refresh table</span>
                    </v-tooltip>
    
                    <v-tooltip bottom>
                        <template #activator="{ on, attrs }">
                            <v-btn
                                color="primary"
                                fab
                                small
                                v-bind="attrs"
                                v-on="on"
                                @click="onClickExcelImport"
                            >
                                <v-icon dark>mdi-microsoft-excel</v-icon>
                            </v-btn>
                        </template>
                        Add recipients from an Excel file
                    </v-tooltip>
    
                    <!-- Full Actions Menu (all sections) -->
                    <rc-feature-discovery-tooltip
                        ref="feature_discovery_tooltip"
                        storage_key="bulk-recipients-actions-menu-v1"
                        message="We've consolidated recipient actions into this improved menu! Click the button below to explore all options."
                        :viewport_trigger_inset_percentage="20"
                        :disabled="!$rc.isBetaFeatureNew('beta-bulk-message-review-step')"
                    >
                        <template #target>
                            <bulk-message-actions-menu
                                ref="bulk_message_actions_menu"
                                :organization_id="organization_id"
                                :bulk_message_id="bulk_message_id"
                                :bulk_message="bulk_message"
                                :message_state="message_state"
                                v-on="$listeners"
                                @menu-opened="onActionsMenuOpened"
                            />
                        </template>
                    </rc-feature-discovery-tooltip>
                </div>
            </template>
        </v-text-field>

        <v-data-table
            :headers="recipient_headers"
            :items="loading_recipients ? [] : recipients"
            :items-per-page="50"
            loading-text="Loading... Please wait"
            class="elevation-1"
            :custom-sort="customRecipientSort"
            :loading="loading_recipients"
            :footer-props="{
                showFirstLastPage: true,
                itemsPerPage: 50,
                disableItemsPerPage: true,
                itemsPerPageOptions: [50, 100],
            }"
            :search="full_text"
            :options.sync="local_table_options"
            :server-items-length="num_recipients_under_current_filter"
        >
            <template #item.recipient_contact_first_name="{ item }">
                {{ prettyFirstName(item) }}
            </template>

            <template #item.recipient_contact_last_name="{ item }">
                {{ prettyLastName(item) }}
            </template>

            <template #item.recipient_endpoint="{ item }">
                {{ $rc.pretty(item.recipient_endpoint, 'endpoint_no_parens') }}
            </template>

            <template #item.locations="{ item }">
                {{ $rc.pretty(item.locations, 'comma_list') }}
            </template>

            <template
                v-if="$rc.$state.isAuthActorSystemAdmin()"
                #header.number_reassignment_check_since_date
            >
                <m-restricted-table-header-cell text="Number Reassignment Check Since Date" />
            </template>
            <template #item.number_reassignment_check_since_date="{ item }">
                {{ $rc.pretty(item.number_reassignment_check_since_date, 'date') }}
            </template>

            <template #item.custom_message="{ item }">
                {{ prettyCustomMessage(item.custom_message) }}
            </template>

            <template #item.status="{ item }">
                {{ prettyStoredMessageState(item) }}
            </template>

            <template #item.action="{ item }">
                <v-menu
                    v-if="getActionMenuItems(item).length > 0"
                    bottom
                    offset-y
                >
                    <template #activator="{ on, attrs }">
                        <v-icon
                            v-bind="attrs"
                            v-on="on"
                        >
                            mdi-dots-vertical-circle-outline
                        </v-icon>
                    </template>

                    <v-list dense>
                        <template v-for="(action, index) in getActionMenuItems(item)">
                            <v-divider
                                v-if="action.divider"
                                :key="'divider-' + index"
                            />
                            <m-restricted-menu-list-item
                                v-else-if="action.restricted"
                                :key="'restricted-' + action.key"
                                :text="action.text"
                                @click="action.handler(item)"
                            />
                            <v-list-item
                                v-else
                                :key="'item-' + action.key"
                                @click="action.handler(item)"
                            >
                                <v-list-item-title>{{ action.text }}</v-list-item-title>
                            </v-list-item>
                        </template>
                    </v-list>
                </v-menu>
            </template>
        </v-data-table>

        <p class="text-caption text--secondary mt-2">* -- State and Timezone are computed on a "best effort" basis based
            on POS / CRM profiles, area code and other data points. Location may be missing or inaccurate.
        </p>

        <!-- keeping in place for now to avoid user confusion -->
        <div class="mt-4 mb-8 text-right">
            <v-btn
                class="mx-2"
                color="primary"
                fab
                small
                disabled
            >
                <v-icon>mdi-plus</v-icon>
            </v-btn>

            <v-btn
                class="mx-2"
                color="primary"
                fab
                small
                disabled
            >
                <v-icon>mdi-account-multiple-plus</v-icon>
            </v-btn>

            <v-btn
                class="mx-2"
                color="primary"
                fab
                small
                disabled
            >
                <v-icon>mdi-microsoft-excel</v-icon>
            </v-btn>

            <v-btn
                class="mx-2"
                color="primary"
                fab
                small
                disabled
            >
                <v-icon>mdi-format-list-bulleted-square</v-icon>
            </v-btn>

            <v-btn
                class="mx-2"
                color="primary"
                fab
                small
                disabled
            >
                <v-icon>mdi-dots-vertical</v-icon>
            </v-btn>

            <p class="text-caption text--secondary pt-2">
                <em>These options have moved to the menu above the table</em>
            </p>
        </div>

        <!-- Dialogs -->
        <m-bulk-recipient-custom-message-dialog
            ref="custom-message-dialog"
            hint="The custom message will override the bulk message text for this recipient only."
            @ok="updateCustomMessageForRecipient"
        />
    </div>
</template>

<style scoped>
.recipients-table-menu
{
    max-height: 70vh;
    min-width: 320px;
    overflow-y: auto;
}
</style>

<script>
import BulkMessageActionsMenu from '@/components/bulk/bulk-message-actions-menu.vue';
const _ = require('lodash');

export default {
    name: 'BulkRecipientsTable',

    components: {
        BulkMessageActionsMenu,
    },

    props: {
        organization_id: {
            type: String,
            required: true,
        },
        bulk_message: {
            type: Object,
            required: false,
            default: null
        },
        bulk_message_id: {
            type: String,
            required: true,
        },
        message_state: {
            type: String,
            required: true,
        },
        loading_recipients: {
            type: Boolean,
            required: true,
        },
        full_text: {
            type: String,
            required: false,
            default: '',
        },
        recipients: {
            type: Array,
            required: true,
        },
        recipient_extra_fields: {
            type: Array,
            required: false,
            default: () => [],
        },
        num_recipients_under_current_filter: {
            type: Number,
            required: true,
        },
        table_options: {
            type: Object,
            required: false,
            default: null,
        },
    },

    data() {
        return {};
    },

    computed: {
        local_table_options: {
            get() {
                return this.table_options;
            },
            set(value) {
                this.$emit('update:table_options', value);
            }
        },

        recipient_headers() {
            const ret = [];

            ret.push({ text: 'First', value: 'recipient_contact_first_name' });
            ret.push({ text: 'Last', value: 'recipient_contact_last_name' });
            ret.push({ text: 'Phone Number', value: 'recipient_endpoint' });
            ret.push({ text: 'Location*', value: 'locations' });

            if (this.$rc.$state.isAuthActorSystemAdmin()) {
                ret.push({ text: 'Number Reassignment Check Since Date', value: 'number_reassignment_check_since_date' });
            }

            if (this.recipient_extra_fields) {
                for (let i = 0; i < this.recipient_extra_fields.length; i++) {
                    let extra_field = {
                        text: this.recipient_extra_fields[i].extra_field_key,
                        value: 'extra_field_' + this.recipient_extra_fields[i].extra_field_key,
                        'sortable': false,
                    };

                    ret.push(extra_field);
                }
            }

            ret.push({ text: 'Custom Message', value: 'custom_message' });

            ret.push({ text: '', value: 'action', 'sortable': false });

            return ret;
        },
    },

    methods: {
        onActionsMenuOpened() {
            if (this.$refs.feature_discovery_tooltip) {
                this.$refs.feature_discovery_tooltip.onDismiss();
            }
        },

        onSearchInputDebounced: _.debounce(function (value) {
            this.$emit('update-full-text', value);
        }, 751),

        genericCompare(first, second, desc) {
            // nulls should be last
            if (first == null && second != null) return 1;
            if (first != null && second == null) return -1;
            if (first == null && second == null) return 0;

            if (!desc) {
                return first > second ? 1 : -1;
            } else {
                return second > first ? 1 : -1;
            }
        },

        customRecipientSort(items, headers, is_desc) {
            // descending information comes in through an array
            const desc = is_desc[0] ? true : false;

            // we don't support multi column, sort, so simply sort on the first header
            const index_header = headers[0];
            if (!index_header) return items;

            items.sort((a, b) => {
                if (index_header === "sent_time") {
                    return this.genericCompare(a.sent_time, b.sent_time, desc);
                }
                if (index_header === "pretty_recipient_endpoint") {
                    return this.genericCompare(a.recipient_endpoint, b.recipient_endpoint, desc);
                }
                if (index_header === "status") {
                    return this.genericCompare(a.state, b.state, desc);
                }
                if (index_header === "replied") {
                    return this.genericCompare(a.replied, b.replied, desc);
                }
                else {
                    return this.genericCompare(a[index_header], b[index_header], desc);
                }
            });

            return items;
        },

        onClickExcelImport() {
            // Use new ImportEngine route by default
            this.$router.push({
                name: 'bulk-message-recipients-import-upload',
                params: {
                    organization_id: this.organization_id,
                },
                query: {
                    bulk_message_id: this.bulk_message_id,
                    return_to: 'view-bulk-message',
                }
            }).catch(e => { }); // swallow any exception
        },

        prettyFirstName(item) {
            if (item.recipient_contact_first_name != null)
                return item.recipient_contact_first_name;

            if (item.extra_fields.first_name != null)
                return item.extra_fields.first_name;

            return null;
        },

        prettyLastName(item) {
            if (item.recipient_contact_last_name != null)
                return item.recipient_contact_last_name;

            if (item.extra_fields.last_name != null)
                return item.extra_fields.last_name;

            return null;
        },

        prettyStoredMessageState(item) {
            if (item.opt_out_state == 'opt-out') {
                return 'Opted Out';
            }

            if (item.state == 'cancelled') {
                return 'Cancelled';
            }

            let state = item.state;

            // edit state checked for backwards compatibility
            if (!state || ['pending', 'edit'].includes(state)) return '';

            if (state == 'sent') {
                state = item.stored_message_state;

                if (state == null || state == '') return '';

                if (state == 'delivered') return 'Delivered';
                if (state == 'in-transit') return 'In Transit';
                if (state == 'error') return 'Error';
            }

            if (state == 'filtered') {
                return 'Filtered';
            }

            return 'Failed';
        },

        prettyCustomMessage(custom_message) {
            if (custom_message == null || custom_message == '') return 'No';
            return 'Yes';
        },

        editCustomMessage(recipient) {
            this.$refs['custom-message-dialog'].showDialog(recipient.bulk_message_recipient_id, recipient.custom_message);
        },

        updateCustomMessageForRecipient(recipient_id, new_message) {
            if (new_message) new_message = new_message.trim();

            this.$rc.updateBulkRecipientCustomMessage(
                recipient_id,
                new_message,
                (data) => {
                    this.$emit('update-recipients');
                },
            );
        },

        onClickEditRecipient(recipient) {
            this.$refs['edit-bulk-recipient'].showDialog(
                this.organization_id,
                this.bulk_message_id,
                recipient
            );
        },

        onClickLocationDetails(item) {
            this.$rc.rest({
                spinner: true,
                endpoint: 'GET: /api/v1/bulk/location/:bulk_message_recipient_id',

                params: {
                    bulk_message_recipient_id: item.bulk_message_recipient_id,
                },

                on_success: (data) => {
                    if (data.technical_details == null) {
                        this.$rc.alert('No location', 'This recipient does not have a location.  Use the "Update recipient locations..." menu item to update locations for all recipients');
                    }
                    else {
                        this.$rc.technicalDetails({
                            title: 'Location Technical Details',
                            technical_details: data.technical_details,
                        });
                    }
                },

                on_error: 'Unable to complete API call',
            });
        },

        onClickNumberReassignmentCheckSinceDateDetails(item) {
            this.$rc.rest({
                spinner: true,
                endpoint: 'GET: /api/v1/bulk/number-reassignment-check-since-date/:bulk_message_recipient_id',

                params: {
                    bulk_message_recipient_id: item.bulk_message_recipient_id,
                },

                on_success: (data) => {
                    if (data.technical_details == null) {
                        this.$rc.alert('No number reassignment check since date', 'This recipient does not have a number reassignment check since date.  Use the "Update number reassignment check since dates..." menu item to update all recipients');
                    }
                    else {
                        this.$rc.technicalDetails({
                            title: 'Number Reassignment Check Since Date Technical Details',
                            technical_details: data.technical_details,
                        });
                    }
                },

                on_error: 'Unable to complete API call',
            });
        },

        deleteRecipient(recipient) {
            this.$emit('delete-recipient', recipient);
        },

        getActionMenuItems(item) {
            const actions = [];

            actions.push({
                key: 'preview-message',
                text: 'Preview Message',
                handler: (item) => this.$emit('preview-message', item),
            });

            actions.push({
                key: 'send-test',
                text: 'Send Test',
                handler: (item) => this.$emit('select-test-message-recipient', item),
            });

            actions.push({
                key: 'custom-message',
                text: 'Custom Message',
                handler: this.editCustomMessage,
            });

            actions.push({
                key: 'edit-recipient',
                text: 'Edit Recipient',
                handler: this.onClickEditRecipient,
            });

            actions.push({
                key: 'location-details',
                text: 'Location technical details',
                handler: this.onClickLocationDetails,
            });

            if (this.$rc.$state.isAuthActorSystemAdmin()) {
                actions.push({
                    key: 'number-reassignment-details',
                    text: 'Number reassignment check since date technical details...',
                    handler: this.onClickNumberReassignmentCheckSinceDateDetails,
                    restricted: true,
                });
            }

            actions.push({ divider: true });

            actions.push({
                key: 'delete-recipient',
                text: 'Delete Recipient',
                handler: this.deleteRecipient,
            });

            return actions;
        },
    },
}
</script>
